// caiet UC91-04 — the stock list. Which columns exist is decided twice: by the
// aggregation level (region only at regional, locality only at locality,
// Registrar / site / lot / expiry date / reason only when nothing is
// aggregated) and by the resolved bucket (Year-Month-Day, Year-Month, Year).
// Unaggregated rows are the stock rows the server read, each under the code it was
// reported under (CF-34), with the level it reported, the stock after the movement
// and not the difference the system stores (CF-55, BUG-1-084); aggregated rows are the
// chart's own points, and say after
// the date when one was carried (UC91-08) or projected (UC91-09). CF-66: the list
// holds every series, and a row whose series is not on the chart says so
// (docs/shape-uc-91-series-and-movements.md §2 D5, D6).
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudIcon, MudTable, MudTag } from '@egov-moldova/mud-react';
import {
  EmptyRegisterMessage,
  formatDate,
  formatExpiry,
  formatNumber,
  MetadataLine,
} from '@simsm/ui-shared';
import {
  correctionDeadline,
  splitMedicineLabel,
  withinCorrectionWindow,
} from '@simsm/shared-types';
import { movementSourceKey } from '../../features/reporting/correction-target';
import type { AggregationLevel } from './stock-filters';
import { placeOf, pointKind, seriesKey, type PointKind } from './stock-series';
import type { Granularity, StockMovement, TimeseriesSeries } from './useStockTimeseries';
import { TableFooter, usePageSize } from '../TableFooter/TableFooter';

interface StockDataTableProps {
  level: AggregationLevel;
  granularity: Granularity;
  series: TimeseriesSeries[];
  movements: StockMovement[];
  // The keys of the series drawn on the chart; a row of any other is tagged.
  charted: ReadonlySet<string>;
  // UC91-04 gives the Registrar column to the System Administrator and to the
  // Observer only: for a Registrar's own people every row carries their own
  // name, so the column is a wasted 140px module and a hint that other
  // Registrars are in scope when they are not.
  canSeeEveryRegistrar: boolean;
  // UC91-16: the Registrar Admin and the Registrar Operator correct a movement
  // from the list; nobody else is offered it or its source (UC91-17).
  canCorrect: boolean;
  labelFor: (medicineCode: string) => string;
}

interface TableRow extends Record<string, unknown> {
  id: string;
  sortKey: string;
  series: string;
  medicineCode: string;
  medicine: string;
  color: string;
  place: string;
  bucket: string;
  quantity: string;
  kind?: PointKind;
  region?: string;
  locality?: string;
  registrar?: string;
  site?: string;
  lot?: string;
  expiry?: string;
  reason?: string;
  correction?: boolean;
}

// UC50-06 (CF-13): the window counts Chisinau calendar days from the acceptance
// date, and the report states its length. A report that states none gets no
// claim either way: the action stays offered and the server decides.
function correctionState(movement: StockMovement): { open: boolean; deadline: string } {
  const windowDays = movement.correctionWindowDays ?? 0;
  const accepted = new Date(movement.confirmedAt ?? '');
  if (windowDays <= 0 || Number.isNaN(accepted.getTime())) {
    return { open: true, deadline: '' };
  }
  return {
    open: withinCorrectionWindow(accepted, windowDays),
    deadline: correctionDeadline(accepted, windowDays),
  };
}

const NO_SERIES_COLOR = 'var(--color-text-base-tertiary)';

function formatBucket(bucket: string, granularity: Granularity): string {
  if (granularity === 'year') return bucket.slice(0, 4);
  if (granularity === 'month') return bucket.slice(0, 7);
  return formatDate(bucket);
}

export function StockDataTable({
  level,
  granularity,
  series,
  movements,
  charted,
  canSeeEveryRegistrar,
  canCorrect,
  labelFor,
}: StockDataTableProps): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize('stock-data');
  const unaggregated = level === 'none';
  // UC91-16: only where no aggregation is made, neither by place nor by time; the
  // wireframe's `randuriMiscari` (Stocuri.tsx:127). per
  // docs/shape-ops-movement-correction-source.md §2 D2
  const correctable = canCorrect && unaggregated && granularity === 'day';

  const dateColumnKey =
    granularity === 'year'
      ? 'stockChart.column.year'
      : granularity === 'month'
        ? 'stockChart.column.year_month'
        : 'stockChart.column.year_month_day';

  const columns = useMemo(() => {
    const defined = [
      { key: 'medicine', label: t('stockChart.column.medicine') },
      level === 'region' ? { key: 'region', label: t('stockChart.column.region') } : null,
      level === 'locality' ? { key: 'locality', label: t('stockChart.column.locality') } : null,
      unaggregated && canSeeEveryRegistrar
        ? { key: 'registrar', label: t('stockChart.column.registrar') }
        : null,
      unaggregated ? { key: 'site', label: t('stockChart.column.site') } : null,
      { key: 'bucket', label: t(dateColumnKey) },
      unaggregated ? { key: 'lot', label: t('stockChart.column.lot') } : null,
      unaggregated ? { key: 'expiry', label: t('stockChart.column.expiry') } : null,
      { key: 'quantity', label: t('stockChart.column.quantity'), align: 'end' as const },
      unaggregated ? { key: 'reason', label: t('stockChart.column.reason') } : null,
      correctable
        ? { key: 'actions', label: t('stockChart.column.actions'), align: 'end' as const }
        : null,
    ];
    return defined.filter((column): column is NonNullable<typeof column> => column !== null);
  }, [canSeeEveryRegistrar, correctable, dateColumnKey, level, t, unaggregated]);

  const rows = useMemo<TableRow[]>(() => {
    if (unaggregated) {
      // The unaggregated level has no places, so a row's series is its series code
      // alone, and its swatch that series' colour.
      const colorOf = new Map(series.map((s) => [seriesKey(s), s.color]));
      const reported = movements.map((movement) => {
        const key = seriesKey({ medicineCode: movement.seriesCode, groupId: '' });
        return {
          id: movement.id,
          sortKey: movement.stockDate,
          series: key,
          medicineCode: movement.medicineCode,
          medicine: movement.medicineLabel,
          color: colorOf.get(key) ?? NO_SERIES_COLOR,
          place: '',
          registrar: movement.registrarName || movement.registrarIdno,
          site: movement.siteName,
          bucket: formatDate(movement.stockDate),
          lot: movement.lot,
          expiry: formatExpiry(movement.lotExpiry, movement.lotExpiryPrecision),
          quantity: formatNumber(movement.quantity),
          reason: t(`stock_reporting.reason.${movement.reason}`),
          correction: movement.isCorrection,
        };
      });
      // UC91-08 (TC-UC91-08-01): the level the series opens with was carried from
      // before the interval, so no movement of the window stands for it. It is listed
      // as a row of its own, tagged, after the movements, which run newest first.
      const carried: TableRow[] = [];
      for (const s of series) {
        const [first] = s.points;
        if (first === undefined || pointKind(first) !== 'carried') continue;
        carried.push({
          id: `${seriesKey(s)}|${first.bucket}|carried`,
          sortKey: first.bucket,
          series: seriesKey(s),
          medicineCode: s.medicineCode,
          medicine: labelFor(s.medicineCode),
          color: s.color,
          place: '',
          bucket: formatBucket(first.bucket, granularity),
          quantity: formatNumber(first.quantity),
          kind: 'carried',
        });
      }
      return [...reported, ...carried];
    }
    // UC91-03: the list holds the points of the chart, so each series contributes
    // its own. Under the regional and locality levels a series is a medicine in
    // one place, and the place is what the Region or Locality column prints.
    const unassigned = t('stockChart.group.unassigned');
    const flat: TableRow[] = [];
    for (const s of series) {
      const place = placeOf(s, unassigned);
      for (const point of s.points) {
        flat.push({
          id: `${seriesKey(s)}|${point.bucket}`,
          sortKey: point.bucket,
          series: seriesKey(s),
          medicineCode: s.medicineCode,
          medicine: labelFor(s.medicineCode),
          color: s.color,
          place,
          ...(level === 'region' ? { region: place } : {}),
          ...(level === 'locality' ? { locality: place } : {}),
          bucket: formatBucket(point.bucket, granularity),
          quantity: formatNumber(point.quantity),
          kind: pointKind(point),
        });
      }
    }
    return flat.sort(
      (a, b) =>
        b.sortKey.localeCompare(a.sortKey) ||
        a.medicine.localeCompare(b.medicine) ||
        a.place.localeCompare(b.place, 'ro'),
    );
  }, [granularity, labelFor, level, movements, series, t, unaggregated]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = rows.slice((safePage - 1) * pageSize, safePage * pageSize);
  const movementByRow = new Map(movements.map((movement) => [movement.id, movement]));

  return (
    <div className="simsm-stocktable">
      <p className="simsm-stocktable__summary">
        {t('stockChart.table.summary', {
          count: rows.length,
          kind: unaggregated
            ? t('stockChart.table.kind_movements')
            : t(`stockChart.table.kind_${granularity}`),
        })}
      </p>
      <MudTable
        className="simsm-table"
        columns={columns}
        rows={pageRows}
        rowStyle="divided"
        hoverable={true}
        disableSort={true}
        aria-label={t('stockChart.table.aria_label')}
      >
        {pageRows.map((row, index) => {
          const identity = splitMedicineLabel(row.medicine);
          return (
            <div
              key={`medicine-${row.id}`}
              slot={`cell-medicine-${index}`}
              className="simsm-stocktable__medicine"
            >
              <div className="simsm-stocktable__medicine-name">
                <span
                  className="simsm-stocktable__swatch"
                  style={{ backgroundColor: row.color }}
                  aria-hidden="true"
                />
                <strong>{identity.name}</strong>
                {/* CF-66 / TC-UC91-03-04: a series past the chart's ceiling, or one
                    the reader hid, is still listed, and says it is not drawn. */}
                {charted.has(row.series) ? null : (
                  <MudTag
                    variant="info"
                    semantic="muted"
                    type="subtle"
                    size="sm"
                    label={t('stockChart.table.not_charted')}
                  />
                )}
              </div>
              <div className="simsm-stocktable__medicine-detail">
                {identity.detail} · {row.medicineCode}
              </div>
            </div>
          );
        })}
        {/* The wireframe tags a carried row "reportat" after its date (Stocuri.tsx,
            `r.reportat`); a projected row is tagged the same way (TC-UC91-09-01), so a
            value read off the list is never taken for a report
            (docs/shape-uc-91-time-window.md §2). */}
        {pageRows.map((row, index) => (
          <span
            key={`bucket-${row.id}`}
            slot={`cell-bucket-${index}`}
            className="simsm-stocktable__bucket"
          >
            {row.bucket}
            {row.kind === 'carried' || row.kind === 'projected' ? (
              <MudTag
                variant="info"
                semantic="muted"
                type="subtle"
                size="sm"
                label={t(`stockChart.table.${row.kind}`)}
              />
            ) : null}
          </span>
        ))}
        {unaggregated
          ? pageRows.map((row, index) => (
              <span key={`reason-${row.id}`} slot={`cell-reason-${index}`}>
                {row.reason}
                {row.correction ? (
                  <MudTag
                    variant="status"
                    semantic="accent"
                    type="subtle"
                    size="sm"
                    label={t('stockChart.table.correction')}
                  />
                ) : null}
                {correctable ? (
                  <SourceLine source={movementByRow.get(row.id)?.source ?? ''} />
                ) : null}
              </span>
            ))
          : null}
        {correctable
          ? pageRows.map((row, index) => {
              const movement = movementByRow.get(row.id);
              // A row that names no movement or no location cannot be corrected
              // by the flow it opens, as on the report detail.
              if (!movement?.id || !movement.siteId) return null;
              const state = correctionState(movement);
              return (
                <span key={`actions-${row.id}`} slot={`cell-actions-${index}`}>
                  <MudButton
                    shape="circular"
                    variant="strict"
                    appearance="text"
                    size="sm"
                    iconOnly
                    label={t('stockChart.table.correct')}
                    disabled={!state.open}
                    title={
                      state.open
                        ? state.deadline
                          ? t('stockChart.table.correct_open', {
                              deadline: formatDate(state.deadline),
                            })
                          : undefined
                        : t('stockChart.table.correct_closed', {
                            days: movement.correctionWindowDays ?? 0,
                            deadline: formatDate(state.deadline),
                          })
                    }
                    onClick={() =>
                      navigate(`../reporting/new?corectie=${encodeURIComponent(movement.id)}`)
                    }
                  >
                    <MudIcon slot="icon" name="edit" size={16} />
                  </MudButton>
                </span>
              );
            })
          : null}
        {/* `simsm-stocktable__empty` had no rule anywhere in the stylesheet, so
            this line was drawing at whatever mud-table's own empty cell gave it. */}
        <EmptyRegisterMessage>{t('stockChart.table.empty')}</EmptyRegisterMessage>
      </MudTable>
      <TableFooter
        total={rows.length}
        page={safePage}
        pageSize={pageSize}
        onPage={setPage}
        onPageSize={setPageSize}
      />
    </div>
  );
}

function SourceLine({ source }: { source: string }): JSX.Element | null {
  const { t } = useTranslate();
  const key = movementSourceKey(source);
  return key ? (
    <MetadataLine items={[{ label: t('submission_detail.meta.source'), value: t(key) }]} />
  ) : null;
}
